import type { DishKind } from "./dishes";
import type { CoffeeBuild } from "./coffee";
import type { Build } from "./food";

// The restaurants. You run one at a time: cook, serve and wash. Harder ones
// unlock with the stars earned at easier ones. A restaurant with no `setup`
// is listed as "coming soon"; a `hidden` one stays in the code, off the list.

export interface MenuItem {
  name: string;
  /**
   * The clean dishes it goes out on. At a `window` restaurant that's the
   * basket you build it in; at a `shelf` one the kitchen takes them itself.
   */
  dishes: DishKind[];
  /** At a `window` restaurant: what goes in the basket. */
  build?: Build;
  /** At a `cafe`: what goes in the paper cup. */
  coffee?: CoffeeBuild;
}

/** Until `until` (a fraction of the shift), an order comes in about every `every` seconds. */
export interface RushSegment {
  until: number;
  every: number;
}

/** A star's requirements. Stars are earned in order: the 2nd needs the 1st's goal met too. */
export interface StarGoal {
  label: string;
  /** Fraction of orders that went out. */
  minServed?: number;
  maxWalkouts?: number;
  maxBreaks?: number;
}

/** A coffee bar's machines and speeds. */
export interface CafeSetup {
  /** Group heads on the espresso machine: shots that can pull at once. */
  groups: number;
  /** Seconds of holding the grinder to dose a portafilter to the brim. */
  grindSeconds: number;
  /** Seconds for a shot to pull to done. */
  shotSeconds: number;
  /** Seconds for a pitcher of milk on the wand to steam to done. */
  steamSeconds: number;
}

/** The cooking stations' sizes and speeds, at a restaurant where you cook. */
export interface KitchenSetup {
  griddleSlots: number;
  fryerSlots: number;
  /** Seconds for one side of a patty to cook through on the flat-top. */
  pattySideSeconds: number;
  /** Seconds for a portion of fries to turn golden. */
  friesSeconds: number;
  /** Seconds to fill a cup at the soda machine (0 or absent: no soda machine). */
  pourSeconds?: number;
}

export interface ShiftSetup {
  /** Which kitchen layout (`kitchen.ts`) the restaurant uses. */
  layout: "dish-pit" | "burger-shack" | "coffee-truck";
  /**
   * How orders go out. `shelf`: the kitchen plates them itself whenever the
   * clean shelf has the dishes (you only wash). `window`: you cook, build
   * each order in a clean basket and hand it out of the pickup window.
   * `cafe`: you make each coffee in a paper cup at the bar and call it out
   * at the window.
   */
  service: "shelf" | "window" | "cafe";
  kitchen?: KitchenSetup;
  cafe?: CafeSetup;
  /** Opening and closing time on the wall clock, in hours (7 = 7 AM). */
  hours: [number, number];
  /** How long the shift lasts in real seconds. */
  shiftSeconds: number;
  /** No new orders after this many seconds. */
  lastOrderAt: number;
  menu: MenuItem[];
  /** Clean dishes on the shelf at opening. Its keys are the kinds this restaurant uses. */
  stock: Partial<Record<DishKind, number>>;
  /** Dirty dishes already waiting in the tub when you clock in. */
  startTub: DishKind[];
  tubCapacity: number;
  rackSlots: number;
  /** How long one machine cycle takes. */
  machineSeconds: number;
  /** How long a ticket waits before the customer walks out. */
  patience: number;
  /** How long a table takes to eat before its dishes come back dirty: [min, max]. */
  eatSeconds: [number, number];
  rush: RushSegment[];
  wage: number;
  /** Tip for an order sent out at once; it shrinks to half as the wait nears `patience`. */
  tip: number;
  /** Docked from your pay for each broken dish. */
  breakCost: number;
  /** Three goals, one per star. */
  goals: [StarGoal, StarGoal, StarGoal];
}

export interface Restaurant {
  id: string;
  name: string;
  tagline: string;
  blurb: string;
  /** Where it is, when it's part of somewhere bigger: "Food truck lot". */
  place?: string;
  /** Total stars (best per listed restaurant, summed) needed to work here. */
  unlockStars: number;
  /** Null: listed, coming soon. */
  setup: ShiftSetup | null;
  /** Kept in the code for later, but not on the list. */
  hidden?: boolean;
}

const burger = (patties: number, toppings: Build["toppings"], fries = false, drink?: Build["drink"]): Build => ({ patties, toppings, fries, ...(drink && { drink }) });
const drinkOnly = (drink: NonNullable<Build["drink"]>): Build => ({ patties: 0, toppings: [], fries: false, drink });

const burgerShack: ShiftSetup = {
  layout: "burger-shack",
  service: "window",
  kitchen: { griddleSlots: 4, fryerSlots: 2, pattySideSeconds: 6, friesSeconds: 8, pourSeconds: 2 },
  hours: [11, 15],
  shiftSeconds: 240,
  lastOrderAt: 185,
  menu: [
    { name: "Hamburger", dishes: ["basket"], build: burger(1, ["lettuce", "tomato", "onion"]) },
    { name: "Hamburger, fries & lemon pop", dishes: ["basket", "tumbler"], build: burger(1, ["lettuce", "tomato", "onion"], true, "lemon") },
    { name: "Cheeseburger", dishes: ["basket"], build: burger(1, ["cheese", "pickles"]) },
    { name: "Cheeseburger, fries & pop", dishes: ["basket", "tumbler"], build: burger(1, ["cheese", "pickles"], true, "pop") },
    { name: "Bacon cheeseburger & fries", dishes: ["basket"], build: burger(1, ["cheese", "bacon", "lettuce"], true) },
    { name: "Double cheeseburger & diet pop", dishes: ["basket", "tumbler"], build: burger(2, ["cheese", "onion", "pickles"], false, "diet") },
    { name: "Fries & water", dishes: ["basket", "tumbler"], build: burger(0, [], true, "water") },
    { name: "Pop", dishes: ["tumbler"], build: drinkOnly("pop") },
    { name: "Lemon pop", dishes: ["tumbler"], build: drinkOnly("lemon") },
  ],
  stock: { basket: 5, tumbler: 6 },
  startTub: ["basket", "basket"],
  tubCapacity: 10,
  rackSlots: 8,
  machineSeconds: 8,
  patience: 90,
  eatSeconds: [25, 40],
  rush: [
    { until: 0.25, every: 30 },
    { until: 0.7, every: 21 },
    { until: 1, every: 27 },
  ],
  wage: 45,
  tip: 3,
  breakCost: 3,
  goals: [
    { label: "Get 70% of orders out", minServed: 0.7 },
    { label: "No more than 2 walkouts and 1 broken basket", maxWalkouts: 2, maxBreaks: 1 },
    { label: "Every order out, nothing broken", maxWalkouts: 0, maxBreaks: 0 },
  ],
};

const latte = (shots: number, syrup?: CoffeeBuild["syrup"]): CoffeeBuild => ({ shots, milk: true, ...(syrup && { syrup }) });

const coffeeTruck: ShiftSetup = {
  layout: "coffee-truck",
  service: "cafe",
  cafe: { groups: 2, grindSeconds: 1.2, shotSeconds: 5, steamSeconds: 5 },
  hours: [7, 11],
  shiftSeconds: 240,
  lastOrderAt: 190,
  menu: [
    { name: "Espresso", dishes: [], coffee: { shots: 1, milk: false } },
    { name: "Double espresso", dishes: [], coffee: { shots: 2, milk: false } },
    { name: "Latte", dishes: [], coffee: latte(1) },
    { name: "Double latte", dishes: [], coffee: latte(2) },
    { name: "Vanilla latte", dishes: [], coffee: latte(1, "vanilla") },
    { name: "Caramel latte", dishes: [], coffee: latte(1, "caramel") },
    { name: "Hazelnut latte", dishes: [], coffee: latte(1, "hazelnut") },
  ],
  // Paper cups go out with the coffee; what you wash is the bar's own glassware.
  stock: { shotglass: 6, pitcher: 5 },
  startTub: [],
  tubCapacity: 8,
  rackSlots: 0,
  machineSeconds: 0,
  patience: 100,
  eatSeconds: [0, 0],
  rush: [
    { until: 0.25, every: 30 },
    { until: 0.7, every: 22 },
    { until: 1, every: 26 },
  ],
  wage: 50,
  tip: 3,
  breakCost: 3,
  goals: [
    { label: "Get 70% of orders out", minServed: 0.7 },
    { label: "No more than 2 walkouts and nothing broken", maxWalkouts: 2, maxBreaks: 0 },
    { label: "Every order out, nothing broken", maxWalkouts: 0, maxBreaks: 0 },
  ],
};

const sunnySide: ShiftSetup = {
  layout: "dish-pit",
  service: "shelf",
  hours: [7, 11],
  shiftSeconds: 180,
  lastOrderAt: 155,
  menu: [
    { name: "Short stack", dishes: ["plate", "mug"] },
    { name: "Two eggs, any style", dishes: ["plate", "mug"] },
    { name: "Oatmeal & berries", dishes: ["bowl", "mug"] },
    { name: "Coffee, black", dishes: ["mug"] },
    { name: "Granola & yogurt", dishes: ["bowl"] },
    { name: "Biscuits & gravy", dishes: ["plate"] },
    { name: "The Big Sunny", dishes: ["plate", "bowl", "mug"] },
  ],
  stock: { plate: 7, mug: 7, bowl: 5 },
  startTub: ["mug", "plate", "bowl"],
  tubCapacity: 14,
  rackSlots: 8,
  machineSeconds: 8,
  patience: 42,
  eatSeconds: [22, 38],
  rush: [
    { until: 0.25, every: 11 },
    { until: 0.65, every: 6 },
    { until: 1, every: 10 },
  ],
  wage: 40,
  tip: 2,
  breakCost: 3,
  goals: [
    { label: "Get 70% of orders out", minServed: 0.7 },
    { label: "No more than 2 walkouts and 1 broken dish", maxWalkouts: 2, maxBreaks: 1 },
    { label: "Every order out, nothing broken", maxWalkouts: 0, maxBreaks: 0 },
  ],
};

const luckyNoodle: ShiftSetup = {
  layout: "dish-pit",
  service: "shelf",
  hours: [21, 25],
  shiftSeconds: 200,
  lastOrderAt: 175,
  menu: [
    { name: "Beef noodle soup", dishes: ["bowl", "sauce"] },
    { name: "Pork dumplings", dishes: ["sauce", "cup"] },
    { name: "Wok-fried noodles", dishes: ["wok", "bowl"] },
    { name: "House fried rice", dishes: ["wok", "bowl"] },
    { name: "Jasmine tea", dishes: ["cup"] },
    { name: "Wonton soup", dishes: ["bowl", "cup"] },
    { name: "Late-night special", dishes: ["wok", "bowl", "sauce"] },
  ],
  stock: { bowl: 7, cup: 6, sauce: 9, wok: 2 },
  startTub: ["sauce", "bowl", "cup", "sauce"],
  tubCapacity: 16,
  rackSlots: 8,
  machineSeconds: 8,
  patience: 38,
  eatSeconds: [20, 34],
  rush: [
    { until: 0.25, every: 11 },
    { until: 0.7, every: 7 },
    { until: 1, every: 10 },
  ],
  wage: 55,
  tip: 2.5,
  breakCost: 4,
  goals: [
    { label: "Get 75% of orders out", minServed: 0.75 },
    { label: "No more than 3 walkouts and 1 broken dish", maxWalkouts: 3, maxBreaks: 1 },
    { label: "Every order out, nothing broken", maxWalkouts: 0, maxBreaks: 0 },
  ],
};

export const RESTAURANTS: readonly Restaurant[] = [
  {
    id: "burger-shack",
    name: "Burger Shack",
    tagline: "Roadside burgers and fries",
    blurb: "A flat-top, a fryer, six toppings and a pickup window. Cook the patties, build the baskets, hand them out, and wash the baskets when they come back.",
    unlockStars: 0,
    setup: burgerShack,
  },
  {
    id: "coffee-truck",
    name: "The Daily Grind",
    tagline: "Espresso truck",
    place: "Food truck lot",
    blurb: "Grind, pull shots, steam milk and pump the syrups in a food truck's galley, call out names at the window, and rinse the glassware between orders.",
    unlockStars: 3,
    setup: coffeeTruck,
  },
  // Dishwashing-only restaurants from before Order Up! cooked. Kept for when
  // they get cooking and serving too.
  {
    id: "sunny-side",
    name: "Sunny Side Diner",
    tagline: "Laid-back breakfast spot",
    blurb: "Plates, mugs and oatmeal bowls, one little dish machine, and regulars in no hurry.",
    unlockStars: 0,
    setup: sunnySide,
    hidden: true,
  },
  {
    id: "lucky-noodle",
    name: "Lucky Noodle",
    tagline: "Late-night noodle bar",
    blurb: "Woks come straight back from the line and only get hand-scrubbed, sauce dishes fill a rack fast, and the crowd after the bars close is hungry.",
    unlockStars: 3,
    setup: luckyNoodle,
    hidden: true,
  },
];

/** The restaurants on the list, in order. */
export const LISTED: readonly Restaurant[] = RESTAURANTS.filter((r) => !r.hidden);

export function restaurantById(id: string): Restaurant | undefined {
  return RESTAURANTS.find((r) => r.id === id);
}
